/**
 * @matchday/juice — motion tokens.
 *
 * ── PRECEDENCE (docs/DECISIONS.md D-007, freeze law) ─────────────────────────
 *  1. packages/ui/tokens (TIMINGS.md + tokens.json/index.ts) — extracted comp
 *     values. COMPS WIN where they collide with briefs.
 *  2. docs/briefs/FIDELITY-PASS.md §4 — the locked choreography tables.
 *  3. docs/briefs/FIRST-BLOOD.md §2 — duration/easing tokens; they stand where
 *     the tables/comps are silent.
 *
 * Timings and easings are the spec, not suggestions (±10% conformance law).
 * The crate HELD BREATH is 480ms of total stillness (D-007: 480, not S8's
 * 180ms) and is never shortened or filled — not by LITE compression, not by
 * anything. See src/ceremonies/engine.ts for the enforcement.
 *
 * ── THE RHYTHM LAW (FIRST BLOOD §2.1) ────────────────────────────────────────
 * User-initiated motion is FAST (T-instant → T-move): "you're in control."
 * Platform-giving motion is SLOW (T-ceremony → T-epic): "this matters."
 * Never invert. A tap must never feel laggy; a reward must never feel cheap.
 */

// Extracted comp tokens — packages/ui/tokens (comps win; re-exported so call
// sites have a single import for colour/easing/duration law).
export { tokens as extracted } from "../../ui/tokens/index.js";
export type { Tokens as ExtractedTokens } from "../../ui/tokens/index.js";

// ── FIRST BLOOD §2.1 duration tokens (ms) — VERBATIM ────────────────────────
export const DUR = {
  /** 90ms — state toggles, tap feedback */
  instant: 90,
  /** 180ms — button presses, small element entrances */
  quick: 180,
  /** 280ms — element position changes, screen-to-screen shared elements */
  move: 280,
  /** 450ms — content block entrances, card flips */
  reveal: 450,
  /** 900ms — reward reveals, badge stamps */
  ceremony: 900,
  /** 1600ms — the chest open, card forge — once-per-flow moments only */
  epic: 1600,
} as const;
export type DurToken = keyof typeof DUR;

// ── FIRST BLOOD §2.2 easing tokens — VERBATIM ───────────────────────────────
export const EASE = {
  /** Confident, decisive — taps, toggles */
  snap: "cubic-bezier(0.2, 0, 0, 1)",
  /** Decelerate in — things arriving */
  enter: "cubic-bezier(0.05, 0.7, 0.1, 1)",
  /** Accelerate out — things leaving */
  exit: "cubic-bezier(0.3, 0, 0.8, 0.15)",
  /** Overshoot/bounce — rewards, pops. MAX 1 per moment */
  over: "cubic-bezier(0.34, 1.56, 0.64, 1)",
  /**
   * Idle loops only. FIRST BLOOD names this "sine in-out"; this bezier is the
   * canonical easeInOutSine approximation for the web.
   */
  breathe: "cubic-bezier(0.37, 0, 0.63, 1)",
} as const;
export type EaseToken = keyof typeof EASE;

// ── Comp-extracted timing shorthands (ms) — from packages/ui/tokens ─────────
// Numeric mirrors of the dominant comp durations (TIMINGS.md global table).
// These are the values the primitives default to where the comps define the
// keyframe (D-007: comps win over FIRST BLOOD's per-primitive durations).
export const COMP_MS = {
  /** DUGOUT row flash — the ONLY motion DUGOUT permits */
  flash: 120,
  /** tiny state toggles */
  microTap: 160,
  /** small reveals */
  quick: 220,
  /** menu / drawer / toast slide */
  panel: 280,
  /** default UI transition (25 uses in comps) */
  base: 300,
  /** stamp landings (md-stamp) */
  stamp: 340,
  /** pop / stamp / reveal choreography (md-pop upper call-site) */
  pop: 400,
  /** glint sweeps */
  glint: 550,
  /** cabinet hero entrance */
  cabHero: 600,
  /** takeover slam / shockwave */
  slam: 700,
  /** plate engrave wipe (md-wipe .4s primary) */
  wipe: 400,
  /** idle breathing loop default (comp range 1.4–2.6s; 1.8s is the FIRST
   *  BLOOD BREATHE value and sits inside the comp-observed range) */
  breathe: 1800,
  /** prefers-reduced-motion clamp (comps clamp ALL motion to .15s) */
  reducedCap: 150,
} as const;

// ── FIDELITY-PASS §4 curves (identical to EASE — the tables cite the same
//    four: snap/enter/exit/over). Comp signature curves for the ceremonies:
export const COMP_EASE = {
  /** settle — THE signature decelerate (30 uses) */
  settle: "cubic-bezier(.05,.7,.1,1)",
  /** back — pop/overshoot (13 uses) */
  back: "cubic-bezier(.34,1.56,.64,1)",
  /** sharp — kick / impact */
  sharp: "cubic-bezier(.2,0,0,1)",
  /** smooth — soft settle */
  smooth: "cubic-bezier(.2,.8,.2,1)",
  /** overshoot-a — stamp landings (cabinetArrival shelf stamp) */
  overshootA: "cubic-bezier(.2,1.3,.4,1)",
  /** cabinetArrival flight segment 1 (lift) */
  arcIn: "cubic-bezier(.3,0,.7,.4)",
  /** cabinetArrival flight segment 2 (land) */
  arcOut: "cubic-bezier(.4,.6,.6,1)",
} as const;

// ── Timer thresholds (FIDELITY-PASS §4 "Timers") ────────────────────────────
// countdown ring volt >2:00 · amber ≤2:00 · red pulse ≤0:30; conic sweep with
// tick ring. Exported so timer UIs never hand-roll the thresholds.
export const TIMER_THRESHOLDS = {
  /** ≤ this many seconds remaining → amber */
  amberAtS: 120,
  /** ≤ this many seconds remaining → red pulse */
  redPulseAtS: 30,
} as const;

// ── Reduced-motion crossfade (FIRST BLOOD §8) ───────────────────────────────
/** Under prefers-reduced-motion all primitives collapse to this crossfade. */
export const REDUCED_CROSSFADE_MS = 200;

// ── LITE degradation (FIRST BLOOD §8 ladder, JUICE-FLUTTER §7) ──────────────
/** LITE tier: ceremony timelines compress 30% (×0.7). Choreography ORDER never
 *  changes; the held breath is NEVER compressed (see ceremonies/engine.ts). */
export const LITE_COMPRESSION = 0.7;

/** Version stamp for the tier cache — bump to force a device re-probe. */
export const JUICE_VERSION = "0.1.0";
